import type { ImpersonatorInfo, OAuthProvider } from '@doss/common-all'
import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import config from '~/common/config/config'
import { getAuthRoute } from '~/common/utils/auth-routes'

export type WorkOSUserInfo = {
  id: string
  email: string
  firstName: string
  lastName: string
}

type SignInResponse = {
  user: WorkOSUserInfo
  /** Present when session was created via WorkOS impersonation */
  impersonator?: ImpersonatorInfo
}

export type WorkOSAuthContextValue = {
  user: WorkOSUserInfo | null
  /** Present when session was created via WorkOS impersonation */
  impersonator: ImpersonatorInfo | null
  isLoading: boolean
  isSignedIn: boolean
  signIn: (email: string, password: string) => Promise<WorkOSUserInfo>
  signInWithOAuth: (
    provider: OAuthProvider,
    options?: { devEmail?: string; state?: string },
  ) => Promise<void>
  signOut: () => void
  error: Error | null
}

const WorkOSAuthContext = createContext<WorkOSAuthContextValue | null>(null)

/**
 * Provider for WorkOS authentication state.
 * Manages auth state in a single location and runs check-session once on mount.
 * All components using useWorkOSAuthContext will share this state.
 *
 * Benefits:
 * - Single check-session request on app load (not N requests)
 * - Shared state across all consumers (no cross-instance sync needed)
 * - Simpler mental model (one source of truth)
 */
export function WorkOSAuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<WorkOSUserInfo | null>(null)
  const [impersonator, setImpersonator] = useState<ImpersonatorInfo | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  /**
   * Checks if there's a valid session cookie and restores user state.
   * Called once on provider mount to restore authentication across page refreshes.
   */
  const checkSession = useCallback(async (): Promise<void> => {
    // Only check session when WorkOS auth provider is active
    // Note: config.authProvider is read from module-level config and doesn't change at runtime
    if (config.authProvider === 'firebase') {
      setUser(null)
      setImpersonator(null)
      setIsLoading(false)
      return
    }

    try {
      const response = await fetch(getAuthRoute('check-session'), {
        method: 'POST',
        credentials: 'include', // Send httpOnly sealed session cookie
      })

      if (!response.ok) {
        // No valid session (401) or other error - user is not authenticated
        setUser(null)
        setImpersonator(null)
        setIsLoading(false)
        return
      }

      const data: SignInResponse = await response.json()
      setUser(data.user)
      setImpersonator(data.impersonator ?? null)
    } catch (err) {
      // Network error or other issue - treat as not authenticated
      setUser(null)
      setImpersonator(null)
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Check for existing session once on mount
  useEffect(() => {
    checkSession()
  }, [checkSession])

  /**
   * Signs in a user with email and password via WorkOS.
   * Backend returns user info + sets sealed session cookie.
   */
  const signIn = useCallback(async (email: string, password: string): Promise<WorkOSUserInfo> => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await fetch(getAuthRoute('sign-in'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Receives httpOnly sealed session cookie
        body: JSON.stringify({ email, password }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || 'Failed to sign in')
      }

      const data: SignInResponse = await response.json()
      setUser(data.user)

      // Notify other tabs/windows to sync their state
      localStorage.setItem(
        'workos_auth_event',
        JSON.stringify({ type: 'signin', user: data.user, timestamp: Date.now() }),
      )

      return data.user
    } catch (err) {
      const authError = err instanceof Error ? err : new Error('Sign in failed')
      setError(authError)
      throw authError
    } finally {
      setIsLoading(false)
    }
  }, [])

  /**
   * Signs out the user by revoking the session and clearing the cookie.
   */
  const signOut = useCallback(() => {
    // Clear state immediately
    setUser(null)
    setImpersonator(null)

    // Notify other tabs to sign out
    localStorage.setItem(
      'workos_auth_event',
      JSON.stringify({ type: 'signout', timestamp: Date.now() }),
    )

    // Call backend to clear httpOnly cookie (fire and forget)
    fetch(getAuthRoute('sign-out'), {
      method: 'POST',
      credentials: 'include',
    }).catch(() => {
      // Ignore errors - we've already cleared local state
    })
  }, [])

  // Listen for auth changes from other tabs (cross-tab sync)
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key !== 'workos_auth_event' || !event.newValue) {
        return
      }

      try {
        const authEvent = JSON.parse(event.newValue)

        if (authEvent.type === 'signout') {
          setUser(null)
        } else if (authEvent.type === 'signin' && authEvent.user) {
          setUser(authEvent.user)
        }
      } catch (err) {
        // Ignore parse errors
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  /**
   * Initiates Google or Microsoft OAuth sign-in through WorkOS.
   */
  const signInWithOAuth = useCallback(
    async (
      provider: OAuthProvider,
      options?: { devEmail?: string; state?: string },
    ): Promise<void> => {
      setIsLoading(true)
      setError(null)

      try {
        // Map OAuthProvider enum to backend route path
        const providerPath = provider === 'GoogleOAuth' ? 'google' : 'microsoft'
        const url = new URL(`${config.apiUrl}/unauthenticated/auth/oauth/${providerPath}/url`)
        if (options?.state) {
          url.searchParams.set('state', options.state)
        }
        if (options?.devEmail && !config.isProduction && !config.isStaging) {
          url.searchParams.set('email', options.devEmail)
        }

        const response = await fetch(url.toString(), {
          method: 'GET',
          credentials: 'include',
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(errorData.message || 'Failed to start OAuth sign in')
        }

        const data: { authorizationUrl: string } = await response.json()
        window.location.href = data.authorizationUrl
      } catch (err) {
        const authError = err instanceof Error ? err : new Error('Sign in failed')
        setError(authError)
        setIsLoading(false)
        throw authError
      }
    },
    [],
  )

  const value: WorkOSAuthContextValue = {
    user,
    impersonator,
    isLoading,
    isSignedIn: !!user,
    signIn,
    signOut,
    signInWithOAuth,
    error,
  }

  return <WorkOSAuthContext.Provider value={value}>{children}</WorkOSAuthContext.Provider>
}

/**
 * Hook for accessing WorkOS authentication state.
 *
 * This hook consumes the WorkOSAuthContext, which means:
 * - Only ONE check-session request on app mount (not N requests per component)
 * - All components share the same auth state
 * - Cross-tab sync happens automatically via the provider
 *
 * Security Model:
 * - ALL tokens stored in httpOnly sealed session cookie (server-managed, encrypted)
 * - Frontend stores ZERO tokens (immune to XSS attacks)
 * - Browser automatically sends cookie with every request (credentials: 'include')
 * - Backend automatically unseals cookie, validates session, and refreshes tokens
 *
 * @returns Auth state and methods (user, isLoading, isSignedIn, signIn, signOut, error)
 * @throws Error if used outside of WorkOSAuthProvider
 *
 * @example
 * const { user, isSignedIn, signIn, signOut } = useWorkOSAuthContext()
 */
export function useWorkOSAuthContext(): WorkOSAuthContextValue {
  const context = useContext(WorkOSAuthContext)

  if (!context) {
    throw new Error('useWorkOSAuthContext must be used within WorkOSAuthProvider')
  }

  return context
}
